const DB_OBJECT: string = 'YekongaSystemDatabase';
const DB_NAME: string = 'DefaultSystemDatabase';

interface TableOptions {
  name: string;
  key?: string;
  indexes?: { name: string; key: string }[];
}

interface DatabaseOptions {
  version?: number;
  tables?: TableOptions[];
}

export class VueDatabase {
  private request: IDBOpenDBRequest | undefined;
  private db_object: string = DB_OBJECT;
  private db_name: string;
  public options: DatabaseOptions;
  private db_version: number = 1;
  private tables: TableOptions[] | undefined;
  public db: IDBDatabase | undefined;

  constructor(name?: string | DatabaseOptions, options?: DatabaseOptions) {
    let _name: string | null = null;
    let _options: DatabaseOptions = {};

    if (typeof name === 'string') {
      _name = name;
      _options = options || {};
    } else {
      _name = null;
      _options = name || {};
    }

    this.db_name = _name || DB_NAME;
    this.options = _options;

    this.init(this.options);
  }

  public init(options: DatabaseOptions): void {
    if (typeof window.indexedDB === 'undefined') {
      window.indexedDB = (window as any).mozIndexedDB || (window as any).webkitIndexedDB || (window as any).msIndexedDB;
    }

    this.db_version = options?.version || 1;
    this.tables = Array.isArray(options?.tables) ? options.tables : [];

    if (window.indexedDB) {
      this.request = window.indexedDB.open(this.db_name, this.db_version);
      this.request.onupgradeneeded = this.onupgradeneeded();
      this.request.onsuccess = this.onsuccess();
      this.request.onerror = this.onerror();
    }
  }

  private onupgradeneeded(): (e: IDBVersionChangeEvent) => void {
    const $this = this;
    return (e: IDBVersionChangeEvent) => {
      const db = (e.target as IDBOpenDBRequest).result;
      if(Array.isArray($this.tables)) {
          for (const table of $this.tables) {
            const test = true;
            if (!db.objectStoreNames.contains(table.name) || test) {
              const primaryKey = table.key || 'uuid';
              const store = db.createObjectStore(table.name, { keyPath: primaryKey });
    
              if (Array.isArray(table.indexes)) {
                for (const ind of table.indexes) {
                  store.createIndex(ind.name, ind.key, { unique: false });
                }
              }
            }
          }
      }
    };
  }

  private onsuccess(): (e: Event) => void {
    const $this = this;
    return (e: Event) => {
      $this.db = (e.target as IDBOpenDBRequest).result;
    };
  }

  private onerror(): (e: Event) => void {
    return (e: Event) => {
      console.error(`There was an error: ${(e.target as IDBRequest).error}`);
      console.log(e);
    };
  }

  public table(name: string): Query {
    return new Query(name, this);
  }
}

export class Query {
  private table: string;
  private parent: VueDatabase;
  private filters: { key: string; value: any; symbol: string }[] = [];

  readonly SYMBOL_EQUAL: string = '=';
  readonly SYMBOL_GREATER: string = '>';
  readonly SYMBOL_GREATER_EQUAL: string = '>=';
  readonly SYMBOL_SMALLER: string = '<';
  readonly SYMBOL_SMALLER_EQUAL: string = '<=';
  readonly SYMBOL_LIKE: string = 'LIKE';

  constructor(name: string, parent: VueDatabase) {
    this.table = name;
    this.parent = parent;
  }

  private db(): Promise<IDBDatabase | null> {
    const $parent = this.parent;
    $parent.init($parent.options);
    return new Promise((resolve) => {
      let i = 0;
      const timer = setInterval(() => {
        if ($parent.db) {
          resolve($parent.db);
          clearInterval(timer);
        }
        if (i > 100) {
          resolve(null);
          clearInterval(timer);
        }
        i++;
      }, 500);
    });
  }

  public async get(): Promise<any[]> {
    const $this = this;
    const db = await $this.db();
    let result: any[] = [];

    try {
      const tx = db!.transaction($this.table, 'readonly');
      const store = tx.objectStore($this.table);

      tx.oncomplete = () => db!.close();
      result = await new Promise((resolve) =>
        store.getAll().onsuccess = (event) => resolve($this.dataFilter((event.target as IDBRequest).result))
      );
    } catch (error) {
      result = [];
    }

    return result;
  }

  public async row(id: string | null = null): Promise<any | null> {
    const $this = this;
    const db = await $this.db();
    let result: any = null;

    try {
      const tx = db!.transaction($this.table, 'readonly');
      const store = tx.objectStore($this.table);
      tx.oncomplete = () => db!.close();
      result = await new Promise((resolve) => {
        if (id) {
          store.get(id).onsuccess = (event) => resolve((event.target as IDBRequest).result);
        } else {
          store.getAll().onsuccess = (event) => {
            const res = $this.dataFilter((event.target as IDBRequest).result).shift();
            resolve(res);
          };
        }
      });
    } catch (error) {
      result = null;
    }

    return result || null;
  }

  public async find(): Promise<any[]> {
    return await this.get();
  }

  public async findOne(id: string): Promise<any | null> {
    return await this.row(id);
  }

  public async create(data: any): Promise<{ status: string }> {
    const $this = this;
    const db = await $this.db();
    if (!data.uuid) data.uuid = $this.uuid();

    try {
      const tx = db!.transaction($this.table, 'readwrite');
      const store = tx.objectStore($this.table);

      store.put(data);
      tx.oncomplete = () => db!.close();
    } catch (error) {
      return { status: 'fail' };
    }

    return { status: 'success' };
  }

  public async update(data: any): Promise<{ status: string }> {
    return this.create(data);
  }

  public async delete(id: string): Promise<{ status: string }> {
    const db = await this.db();
    try {
      const tx = db!.transaction(this.table, 'readwrite');
      const store = tx.objectStore(this.table);

      tx.oncomplete = () => db!.close();
      store.delete(id);
    } catch (error) {}

    return { status: 'success' };
  }

  public whereLike(key: string, arg: any = null): this {
    return this.where(key, 'LIKE', arg);
  }

  public where(key: string, arg1?: string | any, arg2?: any): this {
    let value: any = null;
    let symbol: string = '=';

    if (arg2 === undefined) {
      value = arg1;
    } else {
      value = arg2;
      symbol = arg1;
    }

    if (!Array.isArray(this.filters)) this.filters = [];

    this.filters.push({
      key,
      value,
      symbol,
    });

    return this;
  }

  public async clear(): Promise<{ status: string }> {
    try {
      const db = await this.db();
      const tx = db!.transaction(this.table, 'readwrite');
      const store = tx.objectStore(this.table);

      tx.oncomplete = () => db!.close();
      store.clear();
    } catch (error) {}

    return { status: 'success' };
  }

  private dataFilter(result: any[]): any[] {
    const filters: { [key: string]: { key: string; value: any; symbol: string }[] } = {};
    if (Array.isArray(this.filters)) {
      for (const item of this.filters) {
        if (!Array.isArray(filters[item.key])) filters[item.key] = [];
        filters[item.key].push(item);
      }
    }

    return result.filter((elem) => {
      for (const key in elem) {
        if (Object.prototype.hasOwnProperty.call(elem, key) && Object.prototype.hasOwnProperty.call(filters, key)) {
          const comp = filters[key][0];
          let v1 = elem[key];
          let v2 = comp.value;
          const symbol = comp.symbol;

          if (typeof v1 === 'string') {
            v1 = v1.toLowerCase();
            v2 = v2.toLowerCase();
          }

          if (symbol === this.SYMBOL_EQUAL) {
            if (!(v1 === v2)) return false;
          } else if (symbol === this.SYMBOL_GREATER) {
            if (!(v1 > v2)) return false;
          } else if (symbol === this.SYMBOL_GREATER_EQUAL) {
            if (!(v1 >= v2)) return false;
          } else if (symbol === this.SYMBOL_SMALLER) {
            if (!(v1 < v2)) return false;
          } else if (symbol === this.SYMBOL_SMALLER_EQUAL) {
            if (!(v1 <= v2)) return false;
          } else if (symbol === this.SYMBOL_LIKE) {
            if (!v1.includes(v2)) return false;
          }
        }
      }
      return true;
    });
  }

  private uuid(): string {
    const dt = new Date().getTime();
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
      const r = (dt + Math.random() * 16) % 16 | 0;
      return (c === 'x' ? r : (r & 0x3) | 0x8).toString(16);
    });
  }
}

window.VueDatabase = (name?: string | DatabaseOptions, options?: DatabaseOptions) => new VueDatabase(name, options)
window.YekongaSystemDatabase = (name?: string | DatabaseOptions, options?: DatabaseOptions) => new VueDatabase(name, options)

declare global {
    interface Window {
        VueDatabase: (name?: string | DatabaseOptions, options?: DatabaseOptions) => VueDatabase;
        YekongaSystemDatabase: (name?: string | DatabaseOptions, options?: DatabaseOptions) => VueDatabase;
    }
  }

export default VueDatabase;